// src/pages/Dashboard.jsx — FINAL FULL
// Car(Parking) + Motorcycle + Desk, data real dari /api/dashboard/stats
import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useAuth } from "../contexts/AuthContext.jsx";
import { api } from "../lib/api.js";
import Card from "../components/Card.jsx";
import LoadingSpinner from "../components/LoadingSpinner.jsx";
import {
  Car,
  Monitor,
  Bike,
  Clock,
  User,
  Calendar as CalendarIcon,
  ArrowRight,
  RefreshCcw,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { BOOKING_BASE } from "../constants.js";

/* ================== utils ================== */
const HHMM = (s) => (s && s.length >= 16 ? s.slice(11, 16) : "--:--");
const YMD = (s) => (s && s.length >= 10 ? s.slice(0, 10) : "-");
const cmp = (a, b) => (a < b ? -1 : a > b ? 1 : 0);
const safeArr = (x) => (Array.isArray(x) ? x : []);

function nowJakartaStr() {
  const now = new Date();
  const utcMs = now.getTime() + now.getTimezoneOffset() * 60_000;
  const jkt = new Date(utcMs + 7 * 3_600_000);
  const pad = (n) => (n < 10 ? "0" + n : "" + n);
  return `${jkt.getFullYear()}-${pad(jkt.getMonth() + 1)}-${pad(jkt.getDate())} ${pad(
    jkt.getHours()
  )}:${pad(jkt.getMinutes())}:${pad(jkt.getSeconds())}`;
}

function fmtLastUpdated() {
  const now = new Date();
  return now.toLocaleString("en-GB", { hour12: false });
}
/* =========================================== */

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reloading, setReloading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState("");

  const fetchStats = async (isReload = false) => {
    try {
      if (isReload) setReloading(true);
      const { data } = await api.get("/dashboard/stats");
      setStats(data || {});
      setLastUpdated(fmtLastUpdated());
    } catch (e) {
      console.error("Failed to fetch dashboard:", e);
    } finally {
      setLoading(false);
      setReloading(false);
    }
  };

  useEffect(() => {
    fetchStats(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Active now (string compare WIB) — tetap sama seperti logic kamu
  const activeNow = useMemo(() => {
    const out = { parking: [], motorcycle: [], desk: [] };
    if (!stats) return out;

    const nowJkt = nowJakartaStr();
    const guard = (arr) =>
      safeArr(arr)
        .filter((b) => {
          const s = (b.start_local || b.start_time || "").slice(0, 19);
          const e = (b.end_local || b.end_time || "").slice(0, 19);
          if (s.length !== 19 || e.length !== 19) return false;
          return s <= nowJkt && nowJkt <= e;
        })
        .sort((a, b) => cmp(a.start_local || "", b.start_local || ""));

    out.parking = guard(stats.parkingBookings);
    out.motorcycle = guard(stats.motorcycleBookings);
    out.desk = guard(stats.deskBookings);

    return out;
  }, [stats]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="xl" />
      </div>
    );
  }

  /* ---------- Fancy Book Button ---------- */
  const FancyBookButton = ({ to, variant = "desk" }) => {
    const gradientCls =
      variant === "parking"
        ? "from-amber-500 via-orange-500 to-rose-500"
        : variant === "motorcycle"
        ? "from-sky-500 via-cyan-500 to-indigo-500"
        : "from-emerald-500 via-teal-400 to-cyan-500";

    const glow =
      variant === "parking"
        ? "rgba(251,191,36,0.55)"
        : variant === "motorcycle"
        ? "rgba(56,189,248,0.55)"
        : "rgba(16,185,129,0.55)";

    return (
      <motion.button
        type="button"
        onClick={() => navigate(to)}
        whileHover={{
          scale: 1.05,
          boxShadow: `0 10px 30px 0 ${glow}, 0 0 0 1px rgba(255,255,255,0.06)`,
        }}
        whileTap={{ scale: 0.97 }}
        animate={{ backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"] }}
        transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
        style={{ backgroundSize: "200% 200%" }}
        className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold
          text-white bg-gradient-to-r ${gradientCls}
          border border-white/10 backdrop-blur-sm
          shadow-sm focus:outline-none focus:ring-2 focus:ring-white/20`}
        aria-label="Book Now"
      >
        Book Now
        <motion.span
          initial={{ x: 0 }}
          whileHover={{ x: 3 }}
          transition={{ type: "spring", stiffness: 400, damping: 20 }}
          className="inline-flex"
        >
          <ArrowRight className="h-4 w-4" />
        </motion.span>
      </motion.button>
    );
  };

  const StatCard = ({ title, total, available, icon: Icon, color, delay, to, variant }) => {
    const pct = !total ? 0 : Math.min(100, Math.max(0, (available / total) * 100));

    const iconBg =
      variant === "parking"
        ? "from-amber-500 to-yellow-500"
        : variant === "motorcycle"
        ? "from-sky-500 to-indigo-500"
        : "from-emerald-500 to-green-500";

    const barBg =
      variant === "parking"
        ? "bg-gradient-to-r from-amber-500 to-yellow-500"
        : variant === "motorcycle"
        ? "bg-gradient-to-r from-sky-500 to-indigo-500"
        : "bg-gradient-to-r from-emerald-500 to-green-500";

    return (
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, delay }}
        className="relative"
      >
        <Card className="bg-gradient-to-br from-gray-800/90 to-gray-900/90 border-gray-700 shadow-xl">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-gray-400 text-sm font-medium mb-1">{title}</p>
              <div className="flex items-baseline gap-2">
                <span className={`text-3xl font-extrabold ${color}`}>{available}</span>
                <span className="text-gray-500 text-lg">/ {total}</span>
              </div>
              <p className="text-gray-400 text-xs mt-1">Available Slots</p>
            </div>

            <motion.div
              animate={{ rotate: [0, 4, 0] }}
              transition={{ duration: 2.2, repeat: Infinity }}
              className={`shrink-0 p-3 rounded-2xl bg-gradient-to-br ${iconBg} shadow-lg`}
            >
              <Icon className="h-8 w-8 text-white" />
            </motion.div>
          </div>

          <div className="mt-4 bg-gray-700/70 rounded-full h-2 overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${pct}%` }}
              transition={{ duration: 1.2, delay: delay + 0.2 }}
              className={`h-full rounded-full ${barBg}`}
            />
          </div>

          <div className="mt-5 flex justify-end">
            <FancyBookButton to={to} variant={variant} />
          </div>
        </Card>
      </motion.div>
    );
  };

  const BookingCard = ({ booking, type, index }) => {
    const s = booking.start_local || booking.start_time || "";
    const e = booking.end_local || booking.end_time || "";

    const TypeIcon = type === "parking" ? Car : type === "motorcycle" ? Bike : Monitor;

    const iconColor =
      type === "parking"
        ? "text-amber-400"
        : type === "motorcycle"
        ? "text-sky-400"
        : "text-emerald-400";

    const badgeColor =
      type === "parking"
        ? "bg-amber-100 text-amber-800"
        : type === "motorcycle"
        ? "bg-sky-100 text-sky-800"
        : "bg-emerald-100 text-emerald-800";

    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        whileHover={{ scale: 1.015 }}
        transition={{ duration: 0.25, delay: index * 0.04 }}
        className="bg-gradient-to-r from-gray-800/90 to-gray-900/90 border border-gray-700 rounded-xl p-4 shadow-lg"
      >
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2 min-w-0">
            <TypeIcon className={`h-5 w-5 ${iconColor} shrink-0`} />
            <span className="font-semibold text-white truncate">{booking.resource_name}</span>
          </div>

          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${badgeColor}`}>
            Active
          </span>
        </div>

        <div className="space-y-2 text-sm">
          <div className="flex items-center text-gray-300">
            <User className="h-4 w-4 mr-2 shrink-0" />
            <span className="truncate">Booked by {booking.username}</span>
          </div>

          <div className="flex items-center text-gray-300">
            <CalendarIcon className={`h-4 w-4 mr-2 ${iconColor} shrink-0`} />
            <span className="font-medium">{YMD(s)}</span>
          </div>

          <div className="flex items-center text-gray-300">
            <Clock className="h-4 w-4 mr-2 shrink-0" />
            <span>
              {HHMM(s)} - {HHMM(e)}
            </span>
          </div>
        </div>
      </motion.div>
    );
  };

  // data real (kalau backend sudah dipatch)
  const parkingTotal = stats?.parking?.total || 0;
  const parkingAvail = stats?.parking?.available || 0;

  const motorTotal = stats?.motorcycle?.total || 0;
  const motorAvail = stats?.motorcycle?.available || 0;

  const deskTotal = stats?.desk?.total || 0;
  const deskAvail = stats?.desk?.available || 0;

  return (
    <div className="space-y-8">
      {/* HERO */}
      <motion.div
        initial={{ opacity: 0, y: -14 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl p-6 shadow-xl border border-white/10"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-extrabold text-white mb-2">
              Welcome back, {user?.username || "User"}!
            </h1>
            <p className="text-purple-100">
              Here&apos;s what&apos;s happening with your workspace today
            </p>
            {lastUpdated && <p className="text-white/60 text-xs mt-2">Last updated: {lastUpdated}</p>}
          </div>

          <motion.button
            type="button"
            onClick={() => fetchStats(true)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
            className="shrink-0 inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/15 border border-white/15 text-white text-sm font-semibold"
            aria-label="Refresh"
            disabled={reloading}
          >
            <RefreshCcw className={`h-4 w-4 ${reloading ? "animate-spin" : ""}`} />
            {reloading ? "Refreshing" : "Refresh"}
          </motion.button>
        </div>
      </motion.div>

      {/* STATS */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard
          title="Available Car Slots"
          total={parkingTotal}
          available={parkingAvail}
          icon={Car}
          color="text-amber-400"
          delay={0.1}
          to={`${BOOKING_BASE}/booking?type=parking`}
          variant="parking"
        />

        <StatCard
          title="Available Motorcycle Slots"
          total={motorTotal}
          available={motorAvail}
          icon={Bike}
          color="text-sky-400"
          delay={0.2}
          to={`${BOOKING_BASE}/booking?type=motorcycle`}
          variant="motorcycle"
        />

        <StatCard
          title="Available Desk Slots"
          total={deskTotal}
          available={deskAvail}
          icon={Monitor}
          color="text-emerald-400"
          delay={0.3}
          to={`${BOOKING_BASE}/booking?type=desk`}
          variant="desk"
        />
      </div>

      {/* LISTS */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Car */}
        <motion.div
          initial={{ opacity: 0, x: -16 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.45, delay: 0.25 }}
        >
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <Car className="h-6 w-6 text-amber-400" />
            Today&apos;s Car Bookings (Active Now)
          </h2>
          <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
            {activeNow.parking.length ? (
              activeNow.parking.map((b, i) => (
                <BookingCard key={b.id} booking={b} type="parking" index={i} />
              ))
            ) : (
              <div className="bg-gray-800/70 border border-gray-700 rounded-xl p-8 text-center">
                <Car className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">No active car bookings</p>
              </div>
            )}
          </div>
        </motion.div>

        {/* Motorcycle */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, delay: 0.32 }}
        >
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <Bike className="h-6 w-6 text-sky-400" />
            Today&apos;s Motorcycle Bookings (Active Now)
          </h2>
          <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
            {activeNow.motorcycle.length ? (
              activeNow.motorcycle.map((b, i) => (
                <BookingCard key={b.id} booking={b} type="motorcycle" index={i} />
              ))
            ) : (
              <div className="bg-gray-800/70 border border-gray-700 rounded-xl p-8 text-center">
                <Bike className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">No active motorcycle bookings</p>
              </div>
            )}
          </div>
        </motion.div>

        {/* Desk */}
        <motion.div
          initial={{ opacity: 0, x: 16 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.45, delay: 0.39 }}
        >
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <Monitor className="h-6 w-6 text-emerald-400" />
            Today&apos;s Desk Bookings (Active Now)
          </h2>
          <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
            {activeNow.desk.length ? (
              activeNow.desk.map((b, i) => (
                <BookingCard key={b.id} booking={b} type="desk" index={i} />
              ))
            ) : (
              <div className="bg-gray-800/70 border border-gray-700 rounded-xl p-8 text-center">
                <Monitor className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">No active desk bookings</p>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
